import React from 'react';
import { 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  styled,
  Box,
  LinearProgress,
  Tooltip,
  Chip,
  Avatar,
  Fade,
  Zoom,
  alpha
} from '@mui/material';
import { 
  LocalDining, 
  AttachMoney, 
  Percent, 
  InfoOutlined,
  TrendingUp,
  TrendingDown,
  Warning,
  Calculate,
  Receipt,
  Timeline,
  Speed,
  CheckCircle,
  ErrorOutline
} from '@mui/icons-material';

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  '&::-webkit-scrollbar': {
    width: 4,
    height: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.grey[400],
    borderRadius: 4,
  }
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.75rem',
  background: '#2C3E50',
  color: '#FFFFFF',
  padding: theme.spacing(1, 1.5),
  borderRight: '1px solid rgba(255,255,255,0.1)',
  '&:last-child': { borderRight: 0 },
  whiteSpace: 'nowrap',
  '& .MuiSvgIcon-root': {
    fontSize: '0.9rem',
  },
  cursor: 'help'
}));

const DataCell = styled(TableCell)(({ theme, status }) => ({
  fontWeight: status === 'total' ? 600 : 400,
  fontSize: '0.75rem',
  fontFamily: '"Roboto Mono", monospace',
  padding: theme.spacing(0.75, 1.5),
  borderBottom: '1px solid rgba(0,0,0,0.05)',
  color: status === 'alert' ? '#f44336' : 
         status === 'success' ? '#4CAF50' : 
         theme.palette.text.primary,
}));

const DataBar = styled(LinearProgress)(({ theme, value }) => ({
  height: 2,
  borderRadius: 1,
  width: 40,
  marginLeft: 'auto',
  backgroundColor: theme.palette.grey[200],
  '& .MuiLinearProgress-bar': {
    borderRadius: 1,
    backgroundColor: value > 70 ? '#4CAF50' : value > 50 ? '#FF9800' : '#f44336'
  }
}));

const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#1A2B3C',
    color: '#FFFFFF',
    fontSize: '0.7rem',
    padding: '8px 12px',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    maxWidth: 200,
    textAlign: 'center',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  '& .MuiTooltip-arrow': {
    color: '#1A2B3C',
  }
}));

const ExcelTableFoodCost = ({ data }) => {
  const theme = useTheme();

  const updatedData = data.map((item) => {
    const ht = (item.costPrice * 3).toFixed(3);
    const ttc = (ht * 1.07).toFixed(3);
    const realHT = (item.soldPrice / 1.07).toFixed(3);
    const percentage = realHT && item.costPrice ? 
      ((realHT - item.costPrice) / realHT * 100).toFixed(1) : 0;
    const coef = realHT ? (item.soldPrice / item.costPrice).toFixed(2) : 0;
    const theoreticalPrice = item.costPrice * 3;
    
    return {
      ...item,
      ht: parseFloat(ht),
      ttc: parseFloat(ttc),
      realHT: parseFloat(realHT),
      percentage: parseFloat(percentage),
      coef: parseFloat(coef),
      theoreticalPrice,
      status: percentage < 20 ? 'alert' : percentage < 30 ? 'warning' : 'success'
    };
  });

  // Summary calculations
  const summary = {
    totalRevenue: updatedData.reduce((sum, p) => sum + p.soldPrice, 0),
    avgMargin: updatedData.reduce((sum, p) => sum + p.percentage, 0) / updatedData.length || 0
  };

  const formatCurrency = (value) => {
    return value.toFixed(3) + ' DT';
  };

  // Beautiful tooltip content
  const tooltips = {
    product: {
      title: "Désignation de l'article",
      content: "Nom du produit vendu dans le restaurant"
    },
    cost: {
      title: "Coût matière première",
      content: "Prix d'achat des ingrédients nécessaires pour ce produit"
    },
    theoreticalHT: {
      title: "Prix théorique HT",
      content: "Prix de vente conseillé hors taxes (coût × 3)"
    },
    theoreticalTTC: {
      title: "Prix théorique TTC",
      content: "Prix de vente conseillé toutes taxes comprises (+7% TVA)"
    },
    actual: {
      title: "Prix réel pratiqué",
      content: "Prix de vente réellement appliqué en salle"
    },
    actualHT: {
      title: "Prix réel HT",
      content: "Prix réel hors taxes (calculé avec TVA 7%)"
    },
    coefficient: {
      title: "Coefficient multiplicateur",
      content: "Rapport entre prix de vente et coût matière"
    },
    margin: {
      title: "Marge bénéficiaire",
      content: "Pourcentage de profit sur le prix de vente HT"
    }
  };

  const getMarginTooltip = (percentage) => {
    if (percentage >= 30) {
      return {
        title: "✅ Excellente marge",
        content: "Votre rentabilité est optimale (>30%)"
      };
    } else if (percentage >= 20) {
      return {
        title: "⚠️ Marge correcte",
        content: "Votre rentabilité est acceptable (20-30%)"
      };
    } else {
      return {
        title: "❌ Marge insuffisante",
        content: "Risque de perte, révisez votre prix de vente (<20%)"
      };
    }
  };

  return (
    <Paper elevation={0} sx={{ 
      borderRadius: 2,
      border: '1px solid #E0E0E0',
      overflow: 'hidden'
    }}>
      <StyledTableContainer>
        <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              {/* Produit */}
              <HeaderCell>
                <StyledTooltip 
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {tooltips.product.title}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {tooltips.product.content}
                      </Typography>
                    </Box>
                  } 
                  arrow 
                  placement="top"
                  TransitionComponent={Zoom}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocalDining sx={{ fontSize: '0.9rem' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Produit
                    </Typography>
                    <InfoOutlined sx={{ fontSize: '0.7rem', opacity: 0.7, ml: 0.5 }} />
                  </Box>
                </StyledTooltip>
              </HeaderCell>

              {/* Coût */}
              <HeaderCell>
                <StyledTooltip 
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {tooltips.cost.title}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {tooltips.cost.content}
                      </Typography>
                    </Box>
                  } 
                  arrow 
                  placement="top"
                  TransitionComponent={Zoom}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AttachMoney sx={{ fontSize: '0.9rem' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Coût
                    </Typography>
                  </Box>
                </StyledTooltip>
              </HeaderCell>

              {/* P.HT */}
              <HeaderCell>
                <StyledTooltip 
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {tooltips.theoreticalHT.title}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {tooltips.theoreticalHT.content}
                      </Typography>
                    </Box>
                  } 
                  arrow 
                  placement="top"
                  TransitionComponent={Zoom}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Calculate sx={{ fontSize: '0.9rem' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      P.HT
                    </Typography>
                  </Box>
                </StyledTooltip>
              </HeaderCell>

              {/* P.TTC */}
              <HeaderCell>
                <StyledTooltip 
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {tooltips.theoreticalTTC.title}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {tooltips.theoreticalTTC.content}
                      </Typography>
                    </Box>
                  } 
                  arrow 
                  placement="top"
                  TransitionComponent={Zoom}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Receipt sx={{ fontSize: '0.9rem' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      P.TTC
                    </Typography>
                  </Box>
                </StyledTooltip>
              </HeaderCell>

              {/* P.Réel */}
              <HeaderCell>
                <StyledTooltip 
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {tooltips.actual.title}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {tooltips.actual.content}
                      </Typography>
                    </Box>
                  } 
                  arrow 
                  placement="top"
                  TransitionComponent={Zoom}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AttachMoney sx={{ fontSize: '0.9rem' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      P.Réel
                    </Typography>
                  </Box>
                </StyledTooltip>
              </HeaderCell>

              {/* P.R.HT */}
              <HeaderCell>
                <StyledTooltip 
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {tooltips.actualHT.title}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {tooltips.actualHT.content}
                      </Typography>
                    </Box>
                  } 
                  arrow 
                  placement="top"
                  TransitionComponent={Zoom}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Calculate sx={{ fontSize: '0.9rem' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      P.R.HT
                    </Typography>
                  </Box>
                </StyledTooltip>
              </HeaderCell>

              {/* Coef */}
              <HeaderCell>
                <StyledTooltip 
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {tooltips.coefficient.title}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {tooltips.coefficient.content}
                      </Typography>
                    </Box>
                  } 
                  arrow 
                  placement="top"
                  TransitionComponent={Zoom}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Percent sx={{ fontSize: '0.9rem' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Coef
                    </Typography>
                  </Box>
                </StyledTooltip>
              </HeaderCell>

              {/* Marge */}
              <HeaderCell>
                <StyledTooltip 
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {tooltips.margin.title}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {tooltips.margin.content}
                      </Typography>
                    </Box>
                  } 
                  arrow 
                  placement="top"
                  TransitionComponent={Zoom}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Timeline sx={{ fontSize: '0.9rem' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Marge
                    </Typography>
                  </Box>
                </StyledTooltip>
              </HeaderCell>
            </TableRow>
          </TableHead>
          
          <TableBody>
            {updatedData.slice(0, 5).map((product, index) => {
              const marginTooltip = getMarginTooltip(product.percentage);
              
              return (
                <TableRow 
                  key={product.id}
                  sx={{ 
                    bgcolor: index % 2 === 0 ? '#FFFFFF' : '#FAFAFA',
                    '&:hover': { bgcolor: '#F5F5F5' }
                  }}
                >
                  {/* Produit */}
                  <DataCell>
                    <StyledTooltip 
                      title={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {product.name}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            ID: {product.id?.slice(0, 8)}...
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="right"
                      TransitionComponent={Zoom}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 20, height: 20, bgcolor: alpha(product.status === 'success' ? '#4CAF50' : '#f44336', 0.1) }}>
                          <LocalDining sx={{ fontSize: 12, color: product.status === 'success' ? '#4CAF50' : '#f44336' }} />
                        </Avatar>
                        <Typography variant="caption" noWrap sx={{ maxWidth: 120 }}>
                          {product.name}
                        </Typography>
                      </Box>
                    </StyledTooltip>
                  </DataCell>

                  {/* Coût */}
                  <DataCell align="right">
                    <StyledTooltip 
                      title={`Coût matière: ${formatCurrency(product.costPrice)}`}
                      arrow
                      placement="left"
                      TransitionComponent={Zoom}
                    >
                      <span>{formatCurrency(product.costPrice)}</span>
                    </StyledTooltip>
                  </DataCell>

                  {/* P.HT */}
                  <DataCell align="right">
                    <StyledTooltip 
                      title={`Prix théorique HT (coût × 3): ${formatCurrency(product.ht)}`}
                      arrow
                      placement="left"
                      TransitionComponent={Zoom}
                    >
                      <span>{formatCurrency(product.ht)}</span>
                    </StyledTooltip>
                  </DataCell>

                  {/* P.TTC */}
                  <DataCell align="right">
                    <StyledTooltip 
                      title={`Prix théorique TTC (+7%): ${formatCurrency(product.ttc)}`}
                      arrow
                      placement="left"
                      TransitionComponent={Zoom}
                    >
                      <span>{formatCurrency(product.ttc)}</span>
                    </StyledTooltip>
                  </DataCell>

                  {/* P.Réel */}
                  <DataCell align="right" status={product.status}>
                    <StyledTooltip 
                      title={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {product.soldPrice < product.ht ? '⚠️ Prix inférieur au théorique' : '✅ Prix conforme'}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Réel: {formatCurrency(product.soldPrice)} | Théorique: {formatCurrency(product.ht)}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="left"
                      TransitionComponent={Zoom}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {formatCurrency(product.soldPrice)}
                        {product.soldPrice < product.ht && (
                          <Warning sx={{ fontSize: 12, color: '#f44336' }} />
                        )}
                      </Box>
                    </StyledTooltip>
                  </DataCell>

                  {/* P.R.HT */}
                  <DataCell align="right">
                    <StyledTooltip 
                      title={`Prix réel HT (TVA déduite): ${formatCurrency(product.realHT)}`}
                      arrow
                      placement="left"
                      TransitionComponent={Zoom}
                    >
                      <span>{formatCurrency(product.realHT)}</span>
                    </StyledTooltip>
                  </DataCell>

                  {/* Coef */}
                  <DataCell align="right">
                    <StyledTooltip 
                      title={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Coefficient: {product.coef}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {product.coef > 3 ? 'Excellent multiplicateur' : 'Multiplicateur faible'}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="left"
                      TransitionComponent={Zoom}
                    >
                      <Chip
                        label={product.coef}
                        size="small"
                        sx={{ 
                          height: 20,
                          fontSize: '0.65rem',
                          bgcolor: product.coef > 3 ? alpha('#4CAF50', 0.1) : alpha('#FF9800', 0.1),
                          color: product.coef > 3 ? '#4CAF50' : '#FF9800',
                          fontWeight: 600,
                          cursor: 'help'
                        }}
                      />
                    </StyledTooltip>
                  </DataCell>

                  {/* Marge */}
                  <DataCell align="right">
                    <StyledTooltip 
                      title={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {marginTooltip.title}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {marginTooltip.content}
                          </Typography>
                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                            <Typography variant="caption">
                              Marge: {product.percentage}% | Coût: {formatCurrency(product.costPrice)} | Prix: {formatCurrency(product.soldPrice)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      arrow
                      placement="left"
                      TransitionComponent={Zoom}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 600,
                            color: product.status === 'success' ? '#4CAF50' : 
                                   product.status === 'warning' ? '#FF9800' : '#f44336',
                            cursor: 'help'
                          }}
                        >
                          {product.percentage}%
                        </Typography>
                        {product.percentage >= 30 ? (
                          <CheckCircle sx={{ fontSize: 12, color: '#4CAF50' }} />
                        ) : product.percentage >= 20 ? (
                          <Timeline sx={{ fontSize: 12, color: '#FF9800' }} />
                        ) : (
                          <ErrorOutline sx={{ fontSize: 12, color: '#f44336' }} />
                        )}
                      </Box>
                    </StyledTooltip>
                  </DataCell>
                </TableRow>
              );
            })}

            {/* Summary Row */}
            {updatedData.length > 0 && (
              <TableRow sx={{ bgcolor: '#F5F7FA', borderTop: '1px solid #E0E0E0' }}>
                <DataCell colSpan={4} sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                  <StyledTooltip 
                    title={`${updatedData.length} produits analysés`}
                    arrow
                    placement="top"
                    TransitionComponent={Zoom}
                  >
                    <span>Total: {updatedData.length} produits</span>
                  </StyledTooltip>
                </DataCell>
                <DataCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                  <StyledTooltip 
                    title="Chiffre d'affaires total"
                    arrow
                    placement="top"
                    TransitionComponent={Zoom}
                  >
                    <span>{formatCurrency(summary.totalRevenue)}</span>
                  </StyledTooltip>
                </DataCell>
                <DataCell colSpan={2} />
                <DataCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                  <StyledTooltip 
                    title={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Marge moyenne: {summary.avgMargin.toFixed(1)}%
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {summary.avgMargin >= 30 ? 'Excellente rentabilité' : 
                           summary.avgMargin >= 20 ? 'Rentabilité correcte' : 
                           'Rentabilité insuffisante'}
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="top"
                    TransitionComponent={Zoom}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 600,
                        color: summary.avgMargin >= 30 ? '#4CAF50' : 
                               summary.avgMargin >= 20 ? '#FF9800' : '#f44336',
                        cursor: 'help'
                      }}
                    >
                      {summary.avgMargin.toFixed(1)}%
                    </Typography>
                  </StyledTooltip>
                </DataCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        
        {!updatedData.length && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <LocalDining sx={{ fontSize: 32, color: '#B0BEC5', mb: 1 }} />
            <Typography variant="body2" color="textSecondary">
              Aucune donnée disponible
            </Typography>
          </Box>
        )}
      </StyledTableContainer>
    </Paper>
  );
};

export default ExcelTableFoodCost;